import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { readFileHeaderRow, bestFieldMatch, confidenceLabel } from '../utils/fileFieldMatch';

const API = process.env.REACT_APP_API_URL;

// Rejected/Returned dropped 2026-08-11 — the PM no longer manually passes those
// files to the supplier, so their export buttons (LeadQA.js) and per-type default
// assignment here were both removed.
const EXPORT_TYPES = ['full', 'accepted'];
const TYPE_LABELS = { full: 'Full List', accepted: 'Accepted' };

// Mirrors backend leads.py's DATE_FORMAT_PATTERNS/DEFAULT_DATE_FORMAT exactly —
// applies to every date/datetime Lead field in the export (supplier_delivery_date,
// client_delivery_date, created_at, updated_at).
const DATE_FORMAT_OPTIONS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (e.g. 07/15/2026)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (e.g. 15/07/2026)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (e.g. 2026-07-15)' },
  { value: 'M/D/YY', label: 'M/D/YY — short date (e.g. 7/15/26)' },
];
const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

// File header parsing + fuzzy field matching (for "Auto-configure from a file")
// lives in src/utils/fileFieldMatch.js — shared with LeadDeliverySetup.js's
// DestinationForm, which reuses the same matching primitives in the opposite
// direction (target field -> best-matching uploaded/exported column).

/**
 * ExportConfigPanel — field-selection + custom-label-mapping modal for lead CSV
 * exports. Manages a program's pool of named export configs (create/edit/search
 * across programs and copy in/delete), lets the program assign a *different*
 * config as the default for each of the 4 export types (Full List / Accepted /
 * Rejected / Returned), and can auto-configure the field list + column order +
 * custom labels from an uploaded CSV/XLSX's header row (a client's own template),
 * turning setup into "review the suggested mapping" instead of hand-checking
 * every field.
 *
 * Unlike most components in this library, this one fetches/saves its own data
 * (load/search/copy/save against /export-configs) — an established exception in
 * this codebase for modal-shaped components with a narrow API surface (see
 * ReparentDeleteModal.js).
 *
 * Props:
 *   programId       — number|null. Configs are owned by exactly one program; the
 *                      panel is inert (shows a hint, no picker) until this is set.
 *   fields          — [{key, label, group}] from GET /leads/export-fields.
 *   defaults        — {full, accepted} -> config|null, the
 *                      program's current per-type default assignments (used only
 *                      to seed the panel on open — pass a `key={programId}` on
 *                      this component from the parent so it remounts fresh
 *                      whenever the resolved program changes).
 *   onDefaultsChange — (nextDefaultsMap) => void, called after an assignment
 *                      change so the parent's export buttons stay in sync.
 *   onClose         — () => void
 */
// lead_id (NeTrA Lead ID) is the join key for round-tripping an export back
// through the Lead Status Updater — useful for an internal/re-upload export,
// meaningless (and confusing) on one meant for a client. It used to be
// force-selected and non-removable everywhere; now it's a normal field a PM
// opts into, but every DEFAULT selection (a brand-new config, Reset, the
// empty-fields backfill) still excludes it so a client-facing export doesn't
// carry NeTrA's internal id unless a PM actively chose to include it —
// clicking "Select All" is exactly that choice, so it's the one default that
// still includes it (2026-09-11, real ask: "No client delivery exports
// should have netraID... should only show if the PM selects to include it").
const withoutLeadId = (keys) => keys.filter(k => k !== 'lead_id');

export default function ExportConfigPanel({ programId, fields, defaults, onDefaultsChange, onClose }) {
  const allKeys = useMemo(() => fields.map(f => f.key), [fields]);
  // Default selections (new config, Reset, empty backfill, missing selected_fields)
  // leave out `export_only` fields — Program/Component/Goal Name are opt-in, so
  // a PM ticks them deliberately; "Select All" still includes them.
  // `opt_in` fields (Company LinkedIn) are real columns only some programs use — same treatment.
  const defaultKeys = useMemo(() => fields.filter(f => !f.export_only && !f.opt_in).map(f => f.key), [fields]);
  const grouped = useMemo(() => {
    const order = [];
    const byGroup = {};
    fields.forEach(f => {
      if (!byGroup[f.group]) { byGroup[f.group] = []; order.push(f.group); }
      byGroup[f.group].push(f);
    });
    return order.map(g => [g, byGroup[g]]);
  }, [fields]);

  const [selectedFields, setSelectedFields] = useState(() => withoutLeadId(defaultKeys));
  const [labels, setLabels] = useState({});
  const [configName, setConfigName] = useState('');
  const [dateFormat, setDateFormat] = useState(DEFAULT_DATE_FORMAT);
  const [editingId, setEditingId] = useState(null);
  const [localDefaults, setLocalDefaults] = useState(() => defaults || {});

  // `fields` (GET /leads/export-fields) is fetched async by the parent and may still
  // be empty on first mount if the panel is opened before it resolves — backfill the
  // "all fields" default once it arrives, but only if nothing's been selected yet.
  useEffect(() => {
    if (!editingId && selectedFields.length === 0 && allKeys.length > 0) {
      setSelectedFields(withoutLeadId(defaultKeys));
    }
  }, [allKeys, defaultKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  const [programConfigs, setProgramConfigs] = useState([]);
  const [crossQuery, setCrossQuery] = useState('');
  const [crossResults, setCrossResults] = useState([]);
  const [crossSearching, setCrossSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Id of the config just created/updated via Save/Update — drives the
  // "Set as default for:" quick-assign prompt shown right after saving, so
  // checking fields + naming + saving can immediately take effect on an
  // export button without a separate trip back up to the dropdowns.
  const [justSavedId, setJustSavedId] = useState(null);
  const dragRef = useRef(null);

  // Auto-configure from an uploaded file
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [uploadFileName, setUploadFileName] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadHeaders, setUploadHeaders] = useState(null); // string[] | null
  const [mapping, setMapping] = useState({}); // header -> field key ('' = skip)
  const fileInputRef = useRef(null);

  const refreshProgramConfigs = () => {
    if (!programId) { setProgramConfigs([]); return; }
    axios.get(`${API}/export-configs/`, { params: { program_id: programId } })
      .then(r => setProgramConfigs(r.data || []))
      .catch(() => setProgramConfigs([]));
  };
  useEffect(() => { refreshProgramConfigs(); }, [programId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-program search, lightly debounced.
  useEffect(() => {
    if (!crossQuery.trim()) { setCrossResults([]); return; }
    setCrossSearching(true);
    const t = setTimeout(() => {
      axios.get(`${API}/export-configs/search`, { params: { q: crossQuery.trim() } })
        .then(r => setCrossResults(r.data || []))
        .catch(() => setCrossResults([]))
        .finally(() => setCrossSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [crossQuery]);

  const loadConfigIntoForm = (cfg) => {
    setSelectedFields([...(cfg.selected_fields || withoutLeadId(defaultKeys))]);
    setLabels({ ...(cfg.field_mapping || {}) });
    setConfigName(cfg.name || '');
    setDateFormat(cfg.date_format || DEFAULT_DATE_FORMAT);
    setEditingId(cfg.id);
    setError(null);
    setJustSavedId(null);
  };

  const resetForm = () => {
    setSelectedFields(withoutLeadId(defaultKeys));
    setLabels({});
    setConfigName('');
    setDateFormat(DEFAULT_DATE_FORMAT);
    setEditingId(null);
    setError(null);
    setJustSavedId(null);
  };

  const toggleField = (key) => {
    setSelectedFields(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setJustSavedId(null);
  };

  const toggleGroup = (groupFields, allOn) => {
    const keys = groupFields.map(f => f.key);
    setSelectedFields(prev => allOn
      ? prev.filter(k => !keys.includes(k))
      : [...prev, ...keys.filter(k => !prev.includes(k))]);
    setJustSavedId(null);
  };

  // "Select All" is an explicit PM choice to include everything — the one
  // default that DOES include lead_id, same reasoning as "export all" below.
  const selectAllFields = () => { setSelectedFields([...allKeys]); setJustSavedId(null); };
  const clearAllFields = () => { setSelectedFields([]); setJustSavedId(null); };

  const setLabel = (key, value) => {
    setLabels(prev => ({ ...prev, [key]: value }));
    setJustSavedId(null);
  };

  const reorder = (fromKey, toKey) => {
    setJustSavedId(null);
    setSelectedFields(prev => {
      const next = [...prev];
      const fi = next.indexOf(fromKey), ti = next.indexOf(toKey);
      if (fi < 0 || ti < 0) return prev;
      next.splice(fi, 1);
      next.splice(ti, 0, fromKey);
      return next;
    });
  };

  const fieldLabel = (key) => fields.find(f => f.key === key)?.label || key;

  // Which export types currently default to this config — shown as a badge.
  const defaultTypesFor = (configId) =>
    EXPORT_TYPES.filter(t => localDefaults[t]?.id === configId).map(t => TYPE_LABELS[t]);

  const buildPayload = () => ({
    program_id: programId,
    name: configName.trim(),
    selected_fields: selectedFields,
    field_mapping: Object.fromEntries(Object.entries(labels).filter(([, v]) => v && v.trim())),
    date_format: dateFormat,
  });

  const handleSaveNew = async () => {
    if (!configName.trim() || !selectedFields.length || !programId) return;
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`${API}/export-configs/`, buildPayload());
      loadConfigIntoForm(res.data);
      refreshProgramConfigs();
      setJustSavedId(res.data.id);
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  const handleUpdate = async () => {
    if (!editingId || !selectedFields.length) return;
    setBusy(true); setError(null);
    try {
      const { program_id, ...body } = buildPayload(); // eslint-disable-line no-unused-vars
      const res = await axios.put(`${API}/export-configs/${editingId}`, body);
      loadConfigIntoForm(res.data);
      refreshProgramConfigs();
      setJustSavedId(res.data.id);
      // Any type currently defaulted to this config is now showing stale field
      // data in the parent until refreshed — push the updated object through.
      const next = { ...localDefaults };
      let changed = false;
      EXPORT_TYPES.forEach(t => { if (next[t]?.id === editingId) { next[t] = res.data; changed = true; } });
      if (changed) { setLocalDefaults(next); onDefaultsChange(next); }
    } catch (e) { setError(e.response?.data?.detail || 'Update failed'); }
    setBusy(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this saved export config? Any export type defaulting to it will fall back to all fields.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/export-configs/${id}`);
      if (id === editingId) resetForm();
      refreshProgramConfigs();
      const next = { ...localDefaults };
      let changed = false;
      EXPORT_TYPES.forEach(t => { if (next[t]?.id === id) { next[t] = null; changed = true; } });
      if (changed) { setLocalDefaults(next); onDefaultsChange(next); }
    } catch (e) { setError(e.response?.data?.detail || 'Delete failed'); }
    setBusy(false);
  };

  const handleCopyIn = async (id) => {
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`${API}/export-configs/${id}/copy`, null, { params: { target_program_id: programId } });
      loadConfigIntoForm(res.data);
      refreshProgramConfigs();
      setCrossQuery(''); setCrossResults([]);
    } catch (e) { setError(e.response?.data?.detail || 'Copy failed'); }
    setBusy(false);
  };

  const handleSetDefault = async (type, configIdStr) => {
    setBusy(true); setError(null);
    try {
      if (!configIdStr) {
        await axios.delete(`${API}/export-configs/defaults`, { params: { program_id: programId, export_type: type } });
        const next = { ...localDefaults, [type]: null };
        setLocalDefaults(next);
        onDefaultsChange(next);
      } else {
        const res = await axios.put(`${API}/export-configs/defaults`, null, {
          params: { program_id: programId, export_type: type, config_id: configIdStr },
        });
        const next = { ...localDefaults, [type]: res.data };
        setLocalDefaults(next);
        onDefaultsChange(next);
      }
    } catch (e) { setError(e.response?.data?.detail || 'Failed to set default'); }
    setBusy(false);
  };

  // --- Auto-configure from a file -----------------------------------------

  const handleFileSelected = async (file) => {
    if (!file) return;
    setUploadError(null);
    setUploadFileName(file.name);
    setUploadHeaders(null);
    setMapping({});
    try {
      const headers = await readFileHeaderRow(file);
      if (!headers.length) {
        setUploadError('No header row found in that file.');
        return;
      }
      const initialMapping = {};
      headers.forEach(h => {
        const m = bestFieldMatch(h, fields);
        initialMapping[h] = m ? m.field.key : '';
      });
      setUploadHeaders(headers);
      setMapping(initialMapping);
    } catch (e) {
      setUploadError(e.message || 'Could not read that file — is it a valid CSV or XLSX?');
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFileSelected(file);
    e.target.value = ''; // allow re-selecting the same file later
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setUploadDragOver(false);
    const file = e.dataTransfer.files?.[0];
    handleFileSelected(file);
  };

  const discardUpload = () => {
    setUploadFileName(null);
    setUploadHeaders(null);
    setMapping({});
    setUploadError(null);
  };

  // Applies the confirmed header -> field mapping to the normal field
  // checklist / column order / labels — from here on it behaves exactly like
  // a manual selection, so the rest of the form (review, tweak, save) is
  // unchanged. This is the "confirm" step: nothing is saved to the backend
  // until the PM hits Save/Update further down, same as any manual edit.
  const applyMapping = () => {
    const orderedKeys = [];
    const newLabels = {};
    uploadHeaders.forEach(h => {
      const key = mapping[h];
      if (!key || orderedKeys.includes(key)) return; // skip unmapped/duplicate picks
      orderedKeys.push(key);
      newLabels[key] = h;
    });
    setSelectedFields(orderedKeys);
    setLabels(newLabels);
    setJustSavedId(null);
    discardUpload();
  };

  const mappedFieldKeys = new Set(Object.values(mapping).filter(Boolean));
  const hasDuplicateMapping = uploadHeaders && new Set(Object.values(mapping).filter(Boolean)).size
    < Object.values(mapping).filter(Boolean).length;

  const sectionLabel = { fontWeight: 600, fontSize: '12px', color: '#555', marginTop: '14px', marginBottom: '6px' };
  const smallBtn = { fontSize: '11px', padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '3px', background: '#fff' };
  const colBox = { border: '1px solid #eee', borderRadius: '6px', padding: '10px 12px' };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', border: '1px solid #ccc', borderRadius: '8px',
          width: 'min(980px, 96vw)', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)', fontSize: '12px', padding: '18px 22px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
          <div style={{ fontWeight: 700, fontSize: '16px' }}>Export Settings</div>
          <button onClick={onClose} style={{ ...smallBtn, border: 'none', fontSize: '18px', color: '#888' }}>✕</button>
        </div>

        {!programId && (
          <div style={{ color: '#b03a2e', marginTop: '12px' }}>
            Select a program, component, or goal to configure/save export settings.
          </div>
        )}

        {programId && (
          <>
            {/* Auto-configure from a file */}
            <div style={sectionLabel}>Auto-configure from a file</div>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>
              Drag & drop (or select) a client's CSV/XLSX template — we'll match its column headers to NeTrA fields so you can review and confirm the mapping instead of building it by hand.
            </div>

            {!uploadHeaders && (
              <div
                onDragOver={e => { e.preventDefault(); setUploadDragOver(true); }}
                onDragLeave={() => setUploadDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${uploadDragOver ? '#1a6da8' : '#ccc'}`, borderRadius: '6px',
                  padding: '18px', textAlign: 'center', cursor: 'pointer',
                  background: uploadDragOver ? '#eaf4fb' : '#fafbfc', color: '#666',
                }}
              >
                📄 Drag & drop a .csv or .xlsx file here, or click to browse
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileInputChange} style={{ display: 'none' }} />
              </div>
            )}
            {uploadError && <div style={{ color: '#b03a2e', marginTop: '6px' }}>{uploadError}</div>}

            {uploadHeaders && (
              <div style={{ ...colBox, marginTop: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 600 }}>Review mapping — {uploadFileName}</div>
                  <button onClick={discardUpload} style={smallBtn}>Upload a different file</button>
                </div>
                {hasDuplicateMapping && (
                  <div style={{ background: '#fef9e7', color: '#7d6608', border: '1px solid #f0dca0', borderRadius: '4px', padding: '5px 8px', marginBottom: '8px' }}>
                    Two or more columns are mapped to the same field — only the first will be kept when applied.
                  </div>
                )}
                <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                  {uploadHeaders.map(h => {
                    const guess = bestFieldMatch(h, fields);
                    return (
                      <div key={h} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 90px', gap: '8px', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={h}>{h}</span>
                        <select
                          value={mapping[h] || ''}
                          onChange={e => setMapping(prev => ({ ...prev, [h]: e.target.value }))}
                          style={{ fontSize: '12px', padding: '3px 4px' }}
                        >
                          <option value="">— skip this column —</option>
                          {fields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                        <span style={{ fontSize: '10px', color: !mapping[h] ? '#aaa' : guess ? '#1a7f4b' : '#888' }}>
                          {!mapping[h] ? '—' : guess && guess.field.key === mapping[h] ? confidenceLabel(guess.score) : 'Manual'}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button
                    onClick={applyMapping}
                    disabled={mappedFieldKeys.size === 0}
                    style={{ ...smallBtn, background: mappedFieldKeys.size ? '#1a7f4b' : '#e8edf2', color: mappedFieldKeys.size ? '#fff' : '#aaa', border: 'none', padding: '6px 14px', fontWeight: 600 }}
                  >
                    Apply Mapping ({mappedFieldKeys.size} field{mappedFieldKeys.size === 1 ? '' : 's'})
                  </button>
                  <button onClick={discardUpload} style={smallBtn}>Cancel</button>
                </div>
              </div>
            )}

            {/* Config management — three columns now that there's room */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', marginTop: '14px' }}>
              <div style={colBox}>
                <div style={{ fontWeight: 600, marginBottom: '6px' }}>Default config per export type</div>
                {EXPORT_TYPES.map(type => (
                  <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ flex: '0 0 70px' }}>{TYPE_LABELS[type]}</span>
                    <select
                      value={localDefaults[type]?.id || ''}
                      onChange={e => handleSetDefault(type, e.target.value)}
                      disabled={busy}
                      style={{ flex: 1, minWidth: 0, padding: '4px', fontSize: '12px' }}
                    >
                      <option value="">— none (all fields, no mapping) —</option>
                      {programConfigs.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <div style={colBox}>
                <div style={{ fontWeight: 600, marginBottom: '6px' }}>Load a saved config to edit</div>
                <select
                  value={editingId || ''}
                  onChange={e => {
                    if (!e.target.value) { resetForm(); return; }
                    const cfg = programConfigs.find(c => String(c.id) === e.target.value);
                    if (cfg) loadConfigIntoForm(cfg);
                  }}
                  style={{ width: '100%', padding: '5px', fontSize: '12px', boxSizing: 'border-box' }}
                >
                  <option value="">— all fields, no custom labels —</option>
                  {programConfigs.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <button onClick={resetForm} style={{ ...smallBtn, marginTop: '6px' }}>+ New Config</button>
              </div>

              <div style={colBox}>
                <div style={{ fontWeight: 600, marginBottom: '6px' }}>Apply a config from another program</div>
                <input
                  value={crossQuery}
                  onChange={e => setCrossQuery(e.target.value)}
                  placeholder="Search configs by name…"
                  style={{ width: '100%', padding: '5px', fontSize: '12px', boxSizing: 'border-box' }}
                />
                {crossSearching && <div style={{ color: '#999', marginTop: '4px' }}>Searching…</div>}
                {crossResults.length > 0 && (
                  <div style={{ marginTop: '6px', border: '1px solid #eee', borderRadius: '4px', maxHeight: '110px', overflowY: 'auto' }}>
                    {crossResults.map(c => (
                      <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name} <span style={{ color: '#999' }}>— {c.program_name || `program ${c.program_id}`}</span></span>
                        <button disabled={busy} onClick={() => handleCopyIn(c.id)} style={smallBtn}>Copy →</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={sectionLabel}>Date Format</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <select value={dateFormat} onChange={e => { setDateFormat(e.target.value); setJustSavedId(null); }}
                style={{ padding: '5px', fontSize: '12px' }}>
                {DATE_FORMAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span style={{ fontSize: '11px', color: '#888' }}>Applies to every date field (delivery dates, created/updated timestamps)</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={sectionLabel}>Fields to export ({selectedFields.length}/{allKeys.length})</div>
              <span style={{ display: 'flex', gap: '4px' }}>
                <button onClick={selectAllFields} style={{ ...smallBtn, padding: '1px 6px' }}>Select All</button>
                <button onClick={clearAllFields} style={{ ...smallBtn, padding: '1px 6px' }}>Clear All</button>
              </span>
            </div>
            <div style={{ background: '#fef9e7', color: '#7d6608', border: '1px solid #f0dca0', borderRadius: '4px', padding: '6px 8px', marginBottom: '6px' }}>
              Checking fields here only builds a draft — it has no effect on your exports until you <strong>Save</strong> it below <strong>and</strong> assign it as a default for the export type(s) you want, in the section above.
            </div>
            <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px', padding: '8px' }}>
              {grouped.map(([group, groupFields]) => {
                const allOn = groupFields.every(f => selectedFields.includes(f.key));
                return (
                  <div key={group} style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, color: '#1a6da8' }}>
                      <span>{group}</span>
                      <button onClick={() => toggleGroup(groupFields, allOn)} style={{ ...smallBtn, padding: '1px 6px' }}>
                        {allOn ? 'None' : 'All'}
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', columnGap: '14px' }}>
                      {groupFields.map(f => {
                        const isLeadId = f.key === 'lead_id';
                        const checked = selectedFields.includes(f.key);
                        return (
                          <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '3px 0' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              title={isLeadId ? "NeTrA's own internal id — the join key for re-uploading via Lead Status Updater. Off by default since a client doesn't need it; check it in for an internal-use export." : undefined}
                              onChange={() => toggleField(f.key)}
                            />
                            <span style={{ flex: '0 0 110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label}</span>
                            {checked && (
                              <input
                                value={labels[f.key] || ''}
                                onChange={e => setLabel(f.key, e.target.value)}
                                placeholder={f.label}
                                style={{ flex: 1, minWidth: 0, padding: '2px 5px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '3px' }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedFields.length > 0 && (
              <>
                <div style={sectionLabel}>Column order (drag to reorder)</div>
                <div style={{ maxHeight: '130px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px', columns: '2 260px', columnGap: 0 }}>
                  {selectedFields.map(key => (
                    <div
                      key={key}
                      draggable
                      onDragStart={() => { dragRef.current = key; }}
                      onDragOver={e => e.preventDefault()}
                      onDrop={() => { if (dragRef.current && dragRef.current !== key) reorder(dragRef.current, key); }}
                      style={{ padding: '4px 8px', borderBottom: '1px solid #f5f5f5', cursor: 'grab', display: 'flex', justifyContent: 'space-between', breakInside: 'avoid' }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>⠿ {labels[key] || fieldLabel(key)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={sectionLabel}>Save</div>
            <input
              value={configName}
              onChange={e => { setConfigName(e.target.value); setJustSavedId(null); }}
              placeholder="Config name (e.g. Acme Client Delivery)"
              style={{ width: '100%', padding: '5px', fontSize: '12px', boxSizing: 'border-box', marginBottom: '6px' }}
            />
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button disabled={busy || !configName.trim() || !selectedFields.length} onClick={handleSaveNew} style={smallBtn}>Save as New</button>
              <button disabled={busy || !editingId || !selectedFields.length} onClick={handleUpdate} style={smallBtn}>Update</button>
            </div>

            {justSavedId && (
              <div style={{ background: '#eaf6ec', border: '1px solid #a8d5b5', borderRadius: '4px', padding: '8px', marginTop: '8px' }}>
                <div style={{ marginBottom: '6px' }}>Saved. Set as the default for:</div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {EXPORT_TYPES.map(type => (
                    <button
                      key={type}
                      disabled={busy || localDefaults[type]?.id === justSavedId}
                      onClick={() => handleSetDefault(type, String(justSavedId))}
                      style={smallBtn}
                    >
                      {localDefaults[type]?.id === justSavedId ? `✓ ${TYPE_LABELS[type]}` : TYPE_LABELS[type]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {programConfigs.length > 0 && (
              <>
                <div style={sectionLabel}>Saved configs for this program</div>
                <div style={{ border: '1px solid #eee', borderRadius: '4px' }}>
                  {programConfigs.map(c => {
                    const defaultTypes = defaultTypesFor(c.id);
                    return (
                      <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', borderBottom: '1px solid #f5f5f5' }}>
                        <span>
                          {c.name}
                          {defaultTypes.length > 0 && (
                            <span style={{ color: '#1a6da8', fontSize: '10px' }}> ★ default: {defaultTypes.join(', ')}</span>
                          )}
                        </span>
                        <span style={{ display: 'flex', gap: '4px' }}>
                          <button style={{ ...smallBtn, padding: '1px 6px' }} onClick={() => loadConfigIntoForm(c)}>Edit</button>
                          <button style={{ ...smallBtn, padding: '1px 6px', color: '#b03a2e' }} onClick={() => handleDelete(c.id)}>Delete</button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {error && <div style={{ color: '#b03a2e', marginTop: '8px' }}>{error}</div>}

            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button onClick={onClose} style={{ ...smallBtn, background: '#1a6da8', color: '#fff', border: 'none', padding: '6px 14px', fontWeight: 600 }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
